import { ReactElement, SyntheticEvent, useEffect } from 'react'
import {
  Button,
  DropdownWrapper,
  EditableDropdown,
  FlexWrapper,
  Input,
  Loader,
  PageWrapper,
  SectionTitle,
  TostMessage
} from '../../../../../components'
import strings from '../../../../../locale/en'
import DatePicker from 'react-datepicker'
import {
  downloadStudentActivity,
  updateHasError,
  updateStudentActivity
} from '../../../../../redux/user/actions'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { RootState } from '../../../../../redux/store'
import {
  getBranches,
  getInstitutes,
  getBatches
} from '../../../../../redux/coachingCenter/actions'
import {
  getBatchDropdown,
  getBranchDropdown,
  getInstituteDropdown
} from '../../../../../helpers'
import AdminType from '../../../../../const/admin'
import { resetBranches } from '../../../../../redux/branch/action'
import { resetBatches } from '../../../../../redux/batch/action'
import { ROLE } from '../../../../../helpers/determineMenu'

const StudentActivity = (): ReactElement => {
  const {
    studentActivityData,
    institutes,
    branches,
    batches,
    isLoading,
    hasError = false,
    userDetail,
    role
  } = useSelector(
    (state: RootState) => ({
      studentActivityData: state.user.studentActivity,
      institutes: state.coachingCenter.institutes,
      branches: state.coachingCenter.branches,
      batches: state.coachingCenter.batches,
      isLoading: state.user.isLoading,
      hasError: state.user.hasError,
      userDetail: state.user?.userInfo?.userDetail,
      role: state.user.userInfo?.role
    }),
    shallowEqual
  )
  const dispatch = useDispatch()
  const {
    menu: {
      admin: { studentActivity, selectDate, to }
    },
    batchDetails: {
      batchName,
      branchName,
      instituteName,
      placeholders: { selectInstitute, selectBranchName, selectBatch }
    },
    button: { download },
    error: { downloadStudentActivityFailed }
  } = strings
  const { startDate, toDate, instituteId, branchId, batchId } =
    studentActivityData || {}
  const institutesDropdown = getInstituteDropdown(institutes)
  const branchList = branches ? getBranchDropdown(branches) : []
  const batchList = batches ? getBatchDropdown(batches) : []
  const defaultInstituteId = userDetail?.coachingCenterId || instituteId
  const canDownload =
    startDate && toDate && defaultInstituteId && branchId && batchId

  useEffect(() => {
    if (role === ROLE.INSTITUTE_ADMIN) {
      dispatch(
        getBranches({
          coachingCentreId: defaultInstituteId,
          type: AdminType.STUDENT
        })
      )
    } else {
      if (!institutes.length) {
        dispatch(getInstitutes())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <PageWrapper id="container">
      <FlexWrapper justifyContent="space-between" hasBorder>
        <SectionTitle title={studentActivity} />
      </FlexWrapper>
      <TostMessage
        show={hasError}
        onCloseHandler={() => {
          dispatch(updateHasError(false))
        }}
        message={downloadStudentActivityFailed}
      />
      <FlexWrapper noPadding>
        <DropdownWrapper>
          <DatePicker
            selected={startDate}
            required
            onSelect={(date) => {
              dispatch(
                updateStudentActivity({
                  key: 'startDate',
                  value: date
                })
              )
            }}
            onChange={(date: any) => {
              dispatch(
                updateStudentActivity({
                  key: 'startDate',
                  value: date
                })
              )
            }}
            placeholderText={selectDate}
            customInput={
              <Input
                value={startDate}
                inputType="text"
                placeholder={selectDate}
                onChange={(date) => {
                  dispatch(
                    updateStudentActivity({
                      key: 'startDate',
                      value: date
                    })
                  )
                }}
                suffix={['far', 'calendar']}
                label={selectDate}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={toDate}
            onSelect={(date) => {
              dispatch(
                updateStudentActivity({
                  key: 'toDate',
                  value: date
                })
              )
            }}
            onChange={(date: any) => {
              dispatch(
                updateStudentActivity({
                  key: 'toDate',
                  value: date
                })
              )
            }}
            placeholderText={toDate}
            customInput={
              <Input
                value={toDate}
                inputType="text"
                placeholder={to}
                onChange={(date) => {
                  dispatch(
                    updateStudentActivity({
                      key: 'toDate',
                      value: date
                    })
                  )
                }}
                suffix={['far', 'calendar']}
                label={to}
              />
            }
          />
        </DropdownWrapper>
      </FlexWrapper>
      <FlexWrapper
        justifyContent={
          role === ROLE.INSTITUTE_ADMIN ? 'flex-start' : 'space-around'
        }
      >
        {role !== ROLE.INSTITUTE_ADMIN && (
          <EditableDropdown
            width="30%"
            title={instituteName}
            isRequired
            dropdownList={institutesDropdown}
            handleSelect={(item) => {
              dispatch(
                updateStudentActivity({
                  key: 'instituteId',
                  value: item.id
                })
              )
              dispatch(
                getBranches({
                  coachingCentreId: item.id,
                  type: AdminType.STUDENT
                })
              )
              dispatch(resetBranches())
              dispatch(resetBatches())
            }}
            onBlur={() => {}}
            error={''}
            placeholder={selectInstitute}
          />
        )}
        <EditableDropdown
          width="30%"
          title={branchName}
          isRequired
          dropdownList={branchList}
          handleSelect={(item) => {
            dispatch(
              updateStudentActivity({
                key: 'branchId',
                value: item.id
              })
            )
            dispatch(
              getBatches({
                coachingCentreId: defaultInstituteId,
                branchId: branchId,
                type: AdminType.STUDENT
              })
            )
            dispatch(resetBatches())
          }}
          onBlur={() => {}}
          error={''}
          placeholder={selectBranchName}
        />
        <EditableDropdown
          width="30%"
          title={batchName}
          isRequired
          dropdownList={batchList}
          handleSelect={(item) => {
            dispatch(
              updateStudentActivity({
                key: 'batchId',
                value: item.id
              })
            )
          }}
          onBlur={() => {}}
          error={''}
          placeholder={selectBatch}
        />
      </FlexWrapper>
      <FlexWrapper justifyContent="center">
        {isLoading ? (
          <Loader />
        ) : (
          <Button
            onClick={(e: SyntheticEvent) => {
              e.preventDefault()
              dispatch(downloadStudentActivity())
            }}
            disabled={!canDownload}
          >
            {download}
          </Button>
        )}
      </FlexWrapper>
    </PageWrapper>
  )
}

export default StudentActivity
